/**
 * One lesson across a teacher's classes, or across a school.
 *
 * The page for "where are students struggling?": how the lesson went overall,
 * how marks spread, which questions most students missed and the wrong answer
 * they chose, then every student with their latest attempt.
 *
 * Scope follows the viewer. A teacher sees their managed classes (or the one
 * class in ?classId); a school admin or principal sees their school.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft } from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { viewerSchoolId } from '../../lib/analytics/scope';
import {
  getClassStudents,
  getLessonResults,
  getPassThreshold,
  type RosterStudent,
} from '../../services/performanceService';
import {
  DEFAULT_PASS_THRESHOLD,
  distribution,
  formatDuration,
  formatPercent,
  isValid,
  latestPerStudent,
  lessonTitle,
  questionStats,
  scoreTone,
  summarise,
  type Result,
} from '../../lib/analytics/metrics';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { Skeleton } from '../../Components/ui/skeleton';
import { MetricCard, MetricGrid } from '../../Components/analytics/MetricCard';
import { DistributionChart } from '../../Components/analytics/charts';
import { QuestionAnalytics } from '../../Components/analytics/QuestionAnalytics';
import { DataTable, type Column } from '../../Components/analytics/DataTable';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsNote,
  AnalyticsPanel,
  ChartSkeleton,
  TableSkeleton,
} from '../../Components/analytics/AnalyticsPanel';

interface Row {
  uid: string;
  name: string;
  result: Result | null;
  attempts: number;
}

const LessonAnalyticsPage = () => {
  const { chapterId = '', topicId = '' } = useParams<{ chapterId: string; topicId: string }>();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId');
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [results, setResults] = useState<Result[]>([]);
  const [roster, setRoster] = useState<RosterStudent[] | null>(null);
  const [className, setClassName] = useState<string | null>(null);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const schoolId = viewerSchoolId(profile, searchParams.get('schoolId'));
        if (!schoolId) throw new Error('Your account is not linked to a school.');
        // Teachers are scoped to classes; schools see everything in the school.
        const classIds = classId
          ? [classId]
          : profile.role === 'teacher'
            ? (profile.managed_class_ids ?? [])
            : null;
        if (classIds && classIds.length === 0) {
          if (!cancelled) setResults([]);
          return;
        }
        const [list, students, threshold, classSnap] = await Promise.all([
          getLessonResults({ chapterId, topicId, schoolId, classIds }),
          classIds ? getClassStudents(classIds, schoolId).catch(() => null) : Promise.resolve(null),
          getPassThreshold(schoolId),
          classId ? getDoc(doc(db, 'classes', classId)).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setResults(list);
        setRoster(students);
        setPassThreshold(threshold);
        setClassName(classSnap?.exists() ? classSnap.data()?.class_name ?? null : null);
      } catch (err) {
        console.error('[LessonAnalyticsPage] load failed:', err);
        if (!cancelled) setError((err as Error)?.message || 'This lesson’s results could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile, chapterId, topicId, classId, searchParams]);

  const latest = useMemo(() => latestPerStudent(results), [results]);
  const summary = useMemo(() => summarise(latest, passThreshold), [latest, passThreshold]);
  const bands = useMemo(() => distribution(latest), [latest]);
  const questions = useMemo(() => questionStats(latest), [latest]);
  const title = results.length > 0 ? lessonTitle(results.find((r) => r.lessonName) ?? results[0]) : null;
  const sample = results[0];

  const rows = useMemo<Row[]>(() => {
    const attempts = new Map<string, number>();
    for (const r of results) attempts.set(r.studentId, (attempts.get(r.studentId) ?? 0) + 1);
    const byUid = new Map<string, Row>();
    for (const s of roster ?? []) byUid.set(s.uid, { uid: s.uid, name: s.name, result: null, attempts: 0 });
    for (const r of latest) {
      const row = byUid.get(r.studentId);
      if (row) row.result = r;
      else byUid.set(r.studentId, { uid: r.studentId, name: r.studentName || 'Student', result: r, attempts: 0 });
    }
    for (const row of byUid.values()) row.attempts = attempts.get(row.uid) ?? 0;
    return [...byUid.values()];
  }, [roster, latest, results]);

  const assigned = roster ? roster.length : null;
  const completed = latest.filter(isValid).length;

  const columns: Column<Row>[] = [
    {
      key: 'student',
      header: 'Student',
      sortValue: (r) => r.name,
      cell: (r) => (
        <Link to={`/analytics/students/${r.uid}`} onClick={(e) => e.stopPropagation()} className="font-medium text-foreground hover:text-primary hover:underline">
          {r.name}
        </Link>
      ),
    },
    { key: 'score', header: 'Score', align: 'right', sortValue: (r) => r.result?.marksObtained ?? null, cell: (r) => (r.result ? `${r.result.marksObtained} / ${r.result.maximumMarks}` : '—') },
    {
      key: 'percentage',
      header: 'Percentage',
      align: 'right',
      sortValue: (r) => (r.result && isValid(r.result) ? r.result.percentage : null),
      cell: (r) =>
        r.result && isValid(r.result) ? (
          <span className={`font-semibold ${scoreTone(r.result.percentage, passThreshold)}`}>{formatPercent(r.result.percentage)}</span>
        ) : (
          '—'
        ),
    },
    { key: 'accuracy', header: 'Accuracy', align: 'right', sortValue: (r) => r.result?.accuracy ?? null, cell: (r) => formatPercent(r.result?.accuracy) },
    { key: 'completion', header: 'Completion', align: 'right', hideOnMobile: true, sortValue: (r) => r.result?.completion ?? null, cell: (r) => formatPercent(r.result?.completion) },
    { key: 'attempts', header: 'Attempts', align: 'right', hideOnMobile: true, sortValue: (r) => r.attempts, cell: (r) => r.attempts || '—' },
    { key: 'time', header: 'Time', align: 'right', hideOnMobile: true, sortValue: (r) => r.result?.timeSeconds ?? null, cell: (r) => formatDuration(r.result?.timeSeconds) },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => (r.result ? r.result.status : 'none'),
      cell: (r) =>
        !r.result ? (
          <span className="text-xs text-muted-foreground">Not attempted</span>
        ) : (
          <span className={`text-xs font-medium ${r.result.status === 'completed' ? 'text-foreground' : 'text-amber-500'}`}>
            {r.result.status === 'completed' ? 'Completed' : 'Partial'}
          </span>
        ),
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Lesson analytics</p>
          {loading && !title ? (
            <Skeleton className="mt-2 h-7 w-72" />
          ) : (
            <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
              {title ?? topicId}
            </h1>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            {[sample?.subject, sample?.chapterName, className ?? (classId ? 'One class' : profile?.role === 'teacher' ? 'All my classes' : 'Whole school')]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </header>

        {error && <AnalyticsErrorState message={error} />}

        {!loading && !error && results.length === 0 ? (
          <AnalyticsPanel title="No results for this lesson">
            <AnalyticsEmptyState description="Analytics appear once students in scope complete this lesson’s quiz." />
          </AnalyticsPanel>
        ) : (
          <>
            <MetricGrid>
              <MetricCard
                loading={loading}
                label="Completed"
                value={assigned !== null ? `${completed} / ${Math.max(assigned, completed)}` : completed}
                detail={assigned !== null ? 'Students in class' : 'Students'}
              />
              <MetricCard loading={loading} label="Average" value={formatPercent(summary.average)} detail={`Median ${formatPercent(summary.median)}`} />
              <MetricCard
                loading={loading}
                label="Highest"
                value={formatPercent(summary.highest?.percentage)}
                detail={summary.highest ? `${summary.highest.marksObtained} / ${summary.highest.maximumMarks} marks` : undefined}
              />
              <MetricCard
                loading={loading}
                label="Lowest"
                value={formatPercent(summary.lowest?.percentage)}
                detail={summary.lowest ? `${summary.lowest.marksObtained} / ${summary.lowest.maximumMarks} marks` : undefined}
              />
              <MetricCard loading={loading} label="Accuracy" value={formatPercent(summary.accuracy)} detail={`Completion ${formatPercent(summary.completion)}`} />
              <MetricCard loading={loading} label="Pass rate" value={formatPercent(summary.passRate)} detail={`At or above ${passThreshold}%`} />
            </MetricGrid>

            <div className="grid gap-5 lg:grid-cols-5">
              <AnalyticsPanel className="lg:col-span-2" title="Score distribution" description="Students in each band, latest attempt">
                {loading ? <ChartSkeleton height={220} /> : <DistributionChart bands={bands} />}
              </AnalyticsPanel>
              <AnalyticsPanel className="lg:col-span-3" title="Question analytics" description="Where students struggled">
                {loading ? (
                  <TableSkeleton rows={4} />
                ) : (
                  <QuestionAnalytics questions={questions.questions} coverage={questions.coverage} totalResults={latest.length} />
                )}
              </AnalyticsPanel>
            </div>

            <AnalyticsPanel title="Students" description="Each student’s latest attempt">
              {loading ? (
                <TableSkeleton />
              ) : (
                <DataTable
                  rows={rows}
                  columns={columns}
                  rowKey={(r) => r.uid}
                  initialSort={{ key: 'student', direction: 'asc' }}
                  searchPlaceholder="Search students"
                  searchText={(r) => r.name}
                  onRowClick={(r) => r.result?.id && navigate(`/results/${r.result.id}`)}
                />
              )}
              {roster === null && <AnalyticsNote>Only students who attempted the lesson are listed.</AnalyticsNote>}
            </AnalyticsPanel>
          </>
        )}
      </div>
    </div>
  );
};

export default LessonAnalyticsPage;
